<script setup lang="ts">
import { AFFILIATE } from '~/constants/affiliate'
import { formatPrice, getAllPlans } from '~/constants/pricing'
import type { PremiumPlan, PremiumPlanId } from '~/types/premium'

/**
 * What a partner can earn on: every paid product on sale, what it does for
 * the buyer, where on the site the buyer meets it, its price and the
 * partner's share of it, and the buyer's way through them from the first
 * click. Read from getAllPlans, so a product not on sale (its variant id
 * unset) or retired (the old monthly plan) is not listed, and every price is
 * the one the checkout charges in the reader's currency.
 */
const { t } = useI18n()
const config = useRuntimeConfig()
const { currency } = usePricing()

const rate = `${Math.round(AFFILIATE.commissionRate * 100)} %`

type Group = 'premium' | 'posting' | 'uk'

interface ProductInfo {
  /** Its key under affiliate.products.items. */
  key: string
  group: Group
  icon: string
  /** Where a buyer finds it, when it has a page of its own. */
  to?: string
  /** Its name here, when the checkout's (plan.nameKey) reads oddly out of context. */
  nameKey?: string
}

/** In the order a buyer is most likely to meet them, within each group. */
const PRODUCTS: Partial<Record<PremiumPlanId, ProductInfo>> = {
  'job-pack': { key: 'jobPack', group: 'premium', icon: 'bi-briefcase', to: '/pricing' },
  'pack-monthly': { key: 'packMonthly', group: 'premium', icon: 'bi-arrow-repeat' },
  'yearly': { key: 'yearly', group: 'premium', icon: 'bi-calendar-check', to: '/pricing' },
  'one-time': { key: 'oneTime', group: 'posting', icon: 'bi-file-earmark-pdf', to: '/free-cv-builder', nameKey: 'affiliate.products.items.oneTime.name' },
  'tailored-pdf': { key: 'tailoredPdf', group: 'posting', icon: 'bi-bullseye', to: '/tailor-resume-to-job' },
  'application-kit': { key: 'applicationKit', group: 'posting', icon: 'bi-box-seam', to: '/tailor-resume-to-job' },
  'interview-prep': { key: 'interviewPrep', group: 'posting', icon: 'bi-chat-square-quote' },
  'civil-service': { key: 'civilService', group: 'uk', icon: 'bi-bank', to: '/civil-service-application' },
  'nhs-statement': { key: 'nhsStatement', group: 'uk', icon: 'bi-heart-pulse', to: '/nhs-supporting-statement' },
}
const ORDER = Object.keys(PRODUCTS) as PremiumPlanId[]

interface Row extends ProductInfo {
  plan: PremiumPlan
  price: string
  earn: string
}

const rows = computed<Row[]>(() => getAllPlans(config.public, currency.value)
  .filter(plan => plan.offered && PRODUCTS[plan.id])
  .sort((a, b) => ORDER.indexOf(a.id) - ORDER.indexOf(b.id))
  .map(plan => ({
    ...PRODUCTS[plan.id]!,
    plan,
    price: formatPrice(plan.price, plan.currency),
    earn: formatPrice(Math.floor(plan.price * AFFILIATE.commissionRate * 100) / 100, plan.currency),
  })))

const groups = computed(() => (['premium', 'posting', 'uk'] as const)
  .map(id => ({ id, rows: rows.value.filter(row => row.group === id) }))
  .filter(group => group.rows.length))

const flow = computed(() => (['click', 'build', 'download', 'tailor', 'pack', 'renew'] as const).map(id => t(`affiliate.products.flow.${id}`, { days: AFFILIATE.cookieDays, rate })))
</script>

<template>
  <div class="space-y-12">
    <section>
      <h2 class="!text-xl">{{ t('affiliate.products.title') }}</h2>
      <p class="mt-2 max-w-3xl text-sm text-muted dark:text-gray-400">{{ t('affiliate.products.intro', { rate }) }}</p>

      <div v-for="group in groups" :key="group.id" class="mt-8">
        <h3 class="font-title !text-base font-semibold text-ink dark:text-white">{{ t(`affiliate.products.groups.${group.id}`) }}</h3>
        <div class="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <article
            v-for="row in group.rows"
            :key="row.plan.id"
            class="flex flex-col rounded-[0.625rem] border border-line p-5 dark:border-gray-700"
          >
            <div class="flex items-start gap-3">
              <i :class="['bi', row.icon, 'text-xl text-accent']" aria-hidden="true" />
              <div class="min-w-0 flex-1">
                <h4 class="font-title !text-base font-semibold text-ink dark:text-white">{{ t(row.nameKey ?? row.plan.nameKey) }}</h4>
                <div class="mt-0.5 text-xs text-muted dark:text-gray-400">
                  <template v-if="row.plan.interval">{{ t(`affiliate.products.recurring.${row.plan.interval}`) }}</template>
                  <template v-else>{{ t('affiliate.products.oneTime') }}</template>
                  <template v-if="row.group === 'uk'"> · {{ t('affiliate.products.ukOnly') }}</template>
                </div>
              </div>
            </div>

            <p class="mt-4 text-sm text-body dark:text-gray-300">{{ t(`affiliate.products.items.${row.key}.what`) }}</p>
            <p class="mt-3 text-sm text-body dark:text-gray-300">
              <span class="font-semibold text-ink dark:text-white">{{ t('affiliate.products.where') }}</span>
              {{ t(`affiliate.products.items.${row.key}.where`) }}
            </p>

            <div class="mt-auto pt-5">
              <div class="grid grid-cols-2 gap-3 rounded-lg bg-surface p-3 text-sm dark:bg-gray-800">
                <div>
                  <div class="text-xs text-muted dark:text-gray-400">{{ t('affiliate.products.price') }}</div>
                  <div class="font-title font-bold text-ink dark:text-white">
                    {{ row.price }}<span v-if="row.plan.interval" class="ml-1 font-normal text-muted dark:text-gray-400">{{ t(`affiliate.products.per.${row.plan.interval}`) }}</span>
                  </div>
                </div>
                <div>
                  <div class="text-xs text-muted dark:text-gray-400">{{ t('affiliate.products.earn', { rate }) }}</div>
                  <div class="font-title font-bold text-accent">
                    {{ row.earn }}<span v-if="row.plan.interval" class="ml-1 font-normal text-muted dark:text-gray-400">{{ t(`affiliate.products.per.${row.plan.interval}`) }}</span>
                  </div>
                </div>
              </div>
              <LocaleLink v-if="row.to" :to="row.to" target="_blank" class="link mt-3 inline-flex items-center gap-1 text-sm">
                {{ t('affiliate.products.open') }}
                <i class="bi bi-box-arrow-up-right" aria-hidden="true" />
              </LocaleLink>
            </div>
          </article>
        </div>
      </div>

      <ul class="mt-6 list-disc space-y-1 pl-5 text-xs text-muted dark:text-gray-400">
        <li>{{ t('affiliate.products.notes.discount', { rate }) }}</li>
        <li>{{ t('affiliate.products.notes.credit') }}</li>
        <li v-if="currency !== 'USD'">{{ t('affiliate.products.notes.currency') }}</li>
      </ul>
    </section>

    <section>
      <h2 class="!text-xl">{{ t('affiliate.products.flow.title') }}</h2>
      <p class="mt-2 max-w-3xl text-sm text-muted dark:text-gray-400">{{ t('affiliate.products.flow.intro') }}</p>
      <ol class="mt-6 space-y-4">
        <li v-for="(line, index) in flow" :key="index" class="flex gap-4">
          <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-soft/40 font-title font-bold text-ink dark:bg-accent/20 dark:text-accent-soft">{{ index + 1 }}</span>
          <span class="pt-1 text-sm text-body dark:text-gray-300">{{ line }}</span>
        </li>
      </ol>
      <p class="mt-6 max-w-3xl rounded-[0.625rem] bg-surface p-5 text-sm text-body dark:bg-gray-800 dark:text-gray-300">
        <i class="bi bi-link-45deg mr-1 text-accent" aria-hidden="true" />
        {{ t('affiliate.products.flow.lifetime') }}
      </p>
    </section>
  </div>
</template>
